// components/AI/AILlmKeys.js — Kelola LLM key AI Service (shared) dari browser.
// Admin-only (gerbang permission 'ai_settings' di backend). Key ditampilkan
// ter-mask; nilai utuh tidak pernah dikirim ke browser.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../context/AuthContext';
import { Plus, Trash2, KeyRound, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { AI_PROVIDERS } from './aiConfig';

// DITURUNKAN dari AI_PROVIDERS, bukan daftar terpisah. Sebelumnya daftar ini
// di-hardcode dan tertinggal saat provider baru ditambahkan — akibatnya
// providernya bisa dipilih di AI Configuration tapi TIDAK ADA tempat untuk
// memasukkan API key-nya, dan chat gagal dengan error 401 yang membingungkan.
// Hanya provider yang memang butuh key yang ditampilkan (bedrock pakai
// kredensial AWS; ollama/lmstudio lokal tanpa key).
const PROVIDERS = Object.entries(AI_PROVIDERS)
  .filter(([, p]) => p.requiresKey)
  .map(([id]) => id);

export default function AILlmKeys() {
  const [keys, setKeys]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);

  // form tambah
  const [provider, setProvider] = useState('groq');
  const [apiKey, setApiKey]     = useState('');
  const [label, setLabel]       = useState('');
  const [adding, setAdding]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get('/api/ai/llm-keys');
      setKeys(Array.isArray(data?.keys) ? data.keys : []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Gagal memuat key.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!apiKey.trim()) return;
    setAdding(true); setError(null);
    try {
      await api.post('/api/ai/llm-keys', { provider, api_key: apiKey.trim(), label: label.trim() || undefined });
      setApiKey(''); setLabel('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Gagal menambah key.');
    } finally { setAdding(false); }
  };

  const handleToggle = async (k) => {
    setBusyId(k.id); setError(null);
    try {
      await api.patch(`/api/ai/llm-keys/${k.id}`, { active: !k.active });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Gagal mengubah status key.');
    } finally { setBusyId(null); }
  };

  const handleDelete = async (k) => {
    if (!window.confirm(`Hapus key ${k.label || k.api_key_masked}? Tindakan ini permanen.`)) return;
    setBusyId(k.id); setError(null);
    try {
      await api.delete(`/api/ai/llm-keys/${k.id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Gagal menghapus key.');
    } finally { setBusyId(null); }
  };

  // ringkasan per provider (jumlah aktif)
  const summary = keys.reduce((acc, k) => {
    acc[k.provider] = acc[k.provider] || { total: 0, active: 0 };
    acc[k.provider].total++; if (k.active) acc[k.provider].active++;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <KeyRound style={{ width: 16, height: 16 }} className="text-brand-600" />
            LLM Keys (AI Service)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Key dipakai bersama oleh semua dashboard via AI Service. Rotasi & failover otomatis saat key gagal.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50">
          <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
          Muat ulang
        </button>
      </div>

      {/* ringkasan */}
      {Object.keys(summary).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary).map(([p, s]) => (
            <span key={p} className="px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
              <span className="font-medium text-slate-800">{p}</span> · {s.active}/{s.total} aktif
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">{error}</div>
      )}

      {/* form tambah */}
      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
        <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_140px_auto] gap-2 items-end">
          <label className="block">
            <span className="text-xs text-slate-500">Provider</span>
            <select value={provider} onChange={e => setProvider(e.target.value)}
              className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">API Key</span>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
              placeholder="Tempel API key…" autoComplete="off"
              className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Label (opsional)</span>
            <input value={label} onChange={e => setLabel(e.target.value)}
              placeholder="mis. groq-8"
              className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
          </label>
          <button onClick={handleAdd} disabled={adding || !apiKey.trim()}
            className="flex items-center justify-center gap-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 rounded-lg px-3 py-1.5">
            {adding ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Plus style={{ width: 14, height: 14 }} />}
            Tambah
          </button>
        </div>
      </div>

      {/* daftar key */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-xs">
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> Memuat key…
          </div>
        ) : keys.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">Belum ada key. Tambahkan di atas.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs">
                <th className="text-left font-medium px-3 py-2">Provider</th>
                <th className="text-left font-medium px-3 py-2">Label</th>
                <th className="text-left font-medium px-3 py-2">Key</th>
                <th className="text-center font-medium px-3 py-2">Gagal</th>
                <th className="text-center font-medium px-3 py-2">Status</th>
                <th className="text-right font-medium px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{k.provider}</td>
                  <td className="px-3 py-2 text-slate-500">{k.label || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{k.api_key_masked}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{k.fail_count || 0}</td>
                  <td className="px-3 py-2 text-center">
                    {k.active
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 style={{ width: 13, height: 13 }} /> aktif</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><XCircle style={{ width: 13, height: 13 }} /> nonaktif</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleToggle(k)} disabled={busyId === k.id}
                        className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50">
                        {k.active ? 'Nonaktifkan' : 'Aktifkan'}
                      </button>
                      <button onClick={() => handleDelete(k)} disabled={busyId === k.id}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-50" title="Hapus">
                        {busyId === k.id
                          ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                          : <Trash2 style={{ width: 14, height: 14 }} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
